import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { connect } from "react-redux";
import axios from "axios";
import NumberFormat from "react-number-format";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import {
  getProviderPrograms,
  saveTraining,
} from "../../../../store/cpd/actions";

import {
  showError,
  showSuccess,
  getApiErrorMessage,
} from "../../../toastify";

import {
  serializeTrainingFormDraft,
  hydrateTrainingFormDraft,
  newTargetTraineeRowKey,
  cloneCohortWithNewTargetRowKeys,
} from "../utils/trainingDraftSnapshot";
import {
  downloadVenueTrainersTemplate,
  extractTrainerSearchTermsFromVenueTrainerXlsx,
} from "../utils/venueTrainerExcel";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const trainingCohortDefaults = {
  startDate: null,
  endDate: null,
  numOfTrainees: 0,
  numOfHours: 0,
  budget: 0,
  errors: {},
  venues: [
    {
      venueType: null,
      district: null,
      sectors: [],
      sector: null,
      venues: [],
      venue: null,
      numOfTrainees: 0,
      trainerSearchTerm: "",
      trainers: [],
      errors: {},
    },
  ],
  targetTrainees: [
    {
      district: null,
      sectors: [],
      sector: null,
      schools: [],
      school: null,
      errors: {},
      traineeCategories: [
        {
          category: null,
          subCategories: [],
          subCategory: null,
          subLevel: null,
          subjects: [],
          subject: null,
          numOfTrainees: "",
          errors: {},
        },
      ],
    },
  ],
};

const trainingMaterialDefaults = {
  id: null,
  description: "",
  url: "",
  errors: {},
};

function isNeedsAssessmentUploadApiMessage(text) {
  if (!text) return false;
  const s = text.toLowerCase();
  return (
    s.includes("file too large") ||
    s.includes("invalid file type") ||
    s.includes("needs assessment file")
  );
}

function StepIndicator({ step }) {
  const steps = [
    { n: 1, label: "Training info" },
    { n: 2, label: "Cohorts" },
    { n: 3, label: "Locations" },
    { n: 4, label: "Materials" },
    { n: 5, label: "Review" },
  ];
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
      {steps.map((s) => {
        const state = s.n < step ? "done" : s.n === step ? "active" : "idle";
        const bg =
          state === "done"
            ? "#2e7d32"
            : state === "active"
              ? "#1976d2"
              : "transparent";
        const border =
          state === "idle" ? "1px solid #bdbdbd" : "1px solid transparent";
        const color = state === "idle" ? "#6c757d" : "#fff";
        return (
          <div
            key={s.n}
            className="d-flex align-items-center"
            style={{ gap: 8, minWidth: 150 }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: bg,
                border,
                color,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {state === "done" ? (
                <span className="material-icons" style={{ fontSize: 16 }}>
                  check
                </span>
              ) : (
                s.n
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: state === "active" ? 700 : 500,
                color: state === "active" ? "#1976d2" : "#6c757d",
              }}
            >
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProgressBar({ step }) {
  const pct = Math.min(100, Math.max(0, (step / 5) * 100));
  return (
    <div
      style={{
        height: 3,
        background: "#e0e0e0",
        borderRadius: 999,
        overflow: "hidden",
      }}
    >
      <div style={{ height: 3, width: `${pct}%`, background: "#1976d2" }} />
    </div>
  );
}

const TrainingFormWizardDialog = (props) => {
  const {
    loading,
    user,
    districts,
    providerPrograms,
    traineeCategories,
    schoolSubLevels,
    venueTypes,
    getProviderPrograms,
    saveTraining,
    showTrainingFormDialog,
    setShowTrainingFormDialog,
    resumeDraftId,
    resumeFormSnapshot,
    resumeNeedsAssessmentFilePath,
    onClearResumeDraft,
  } = props;

  useEffect(() => {
    getProviderPrograms(user.id);
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [providerProgram, setProviderProgram] = useState(null);
  const [subPrograms, setSubPrograms] = useState([]);
  const [subProgram, setSubProgram] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [title, setTitle] = useState("");

  const [trainingCohorts, setTrainingCohorts] = useState(() => [
    cloneCohortWithNewTargetRowKeys(trainingCohortDefaults),
  ]);
  const [trainingMaterials, setTrainingMaterials] = useState([
    { ...trainingMaterialDefaults },
  ]);
  const [needsAssessment, setNeedsAssessment] = useState({
    file: null,
    description: "",
  });

  const [errors, setErrors] = useState({
    titleError: "",
    programError: "",
    subProgramError: "",
    topicsError: "",
    descriptionError: "",
    fileError: "",
    hasError: false,
  });

  const [activeDraftId, setActiveDraftId] = useState(null);
  const [persistedNeedsAssessmentPath, setPersistedNeedsAssessmentPath] =
    useState(null);
  const [draftLinkedProviderProgramId, setDraftLinkedProviderProgramId] =
    useState(null);
  const [draftLinkedSubProgramId, setDraftLinkedSubProgramId] = useState(null);
  const [draftLinkedTopicIds, setDraftLinkedTopicIds] = useState(null);

  // Step 3 UI state
  const [activeCohortIndex, setActiveCohortIndex] = useState(0);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("all"); // all | has | missing
  const [locationPage, setLocationPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVenueIndex, setDrawerVenueIndex] = useState(null);
  const [drawerVenueDraft, setDrawerVenueDraft] = useState(null);

  // Step 2: per-target-location school lookup state (search by name/code)
  const [schoolLookupByRowKey, setSchoolLookupByRowKey] = useState({});
  const schoolLookupTimersRef = useRef({});
  const venueTrainerExcelInputRef = useRef(null);

  // When opening the editor with prefilled district/sector (e.g. copied from targets),
  // load dependent option lists so Autocompletes show choices like in the old form.
  useEffect(() => {
    if (!drawerOpen || !drawerVenueDraft) return;

    if (drawerVenueDraft?.district?.id && (drawerVenueDraft.sectors || []).length === 0) {
      getApiData("/api/posts/sectors", drawerVenueDraft.district.id, (data) => {
        setDrawerVenueDraft((prev) => {
          if (!prev) return prev;
          // Don't clobber user edits if they already loaded
          if ((prev.sectors || []).length > 0) return prev;
          return { ...prev, sectors: data || [] };
        });
      });
    }

    if (drawerVenueDraft?.sector?.id && (drawerVenueDraft.venues || []).length === 0) {
      getApiData("/api/cpd/venues", drawerVenueDraft.sector.id, (data) => {
        setDrawerVenueDraft((prev) => {
          if (!prev) return prev;
          if ((prev.venues || []).length > 0) return prev;
          return { ...prev, venues: data || [] };
        });
      });
    }
  }, [
    drawerOpen,
    drawerVenueDraft?.district?.id,
    drawerVenueDraft?.sector?.id,
  ]);

  const formValidator = () => {
    const error = {
      titleError: "",
      programError: "",
      subProgramError: "",
      topicsError: "",
      descriptionError: "",
      fileError: "",
      hasError: false,
    };

    if (!String(title || "").trim()) {
      error.titleError = "Required";
      error.hasError = true;
    }

    if (!providerProgram?.id) {
      error.programError = "Required";
      error.hasError = true;
    }

    if (!subProgram?.id) {
      error.subProgramError = "Required";
      error.hasError = true;
    }

    if (!(selectedTopics || []).some((t) => !!t?.id)) {
      error.topicsError = "Required";
      error.hasError = true;
    }

    if (!needsAssessment.description) {
      error.descriptionError = "Required";
      error.hasError = true;
    }

    if (!needsAssessment.file && !persistedNeedsAssessmentPath) {
      error.fileError = "Required";
      error.hasError = true;
    }

    const tmpTrainingCohorts = [...trainingCohorts];

    tmpTrainingCohorts?.forEach((cohot) => {
      cohot.errors.startDate = !cohot.startDate ? "Required" : undefined;
      cohot.errors.endDate = !cohot.endDate ? "Required" : undefined;
      cohot.errors.numOfTrainees = !cohot.numOfTrainees ? "Required" : undefined;
      cohot.errors.numOfHours = !cohot.numOfHours ? "Required" : undefined;
      cohot.errors.budget = !cohot.budget ? "Required" : undefined;

      for (const errorKey in cohot.errors) {
        if (!!cohot.errors[errorKey]) error.hasError = true;
      }

      let totalNumOfTrainees = 0;
      cohot.targetTrainees?.forEach((targetTrainee, targetTraineeIndex) => {
        targetTrainee.errors.district = !targetTrainee.district
          ? "Required"
          : undefined;
        targetTrainee.errors.sector = !targetTrainee.sector
          ? "Required"
          : undefined;
        targetTrainee.errors.school = !targetTrainee.school
          ? "Required"
          : undefined;

        for (const errorKey in targetTrainee.errors) {
          if (!!targetTrainee.errors[errorKey]) error.hasError = true;
        }

        targetTrainee.traineeCategories?.forEach(
          (traineeCategory, traineeCategoryIndex) => {
            totalNumOfTrainees += +traineeCategory.numOfTrainees;

            traineeCategory.errors.category = !traineeCategory.category
              ? "Required"
              : undefined;

            const subCats = traineeCategory.subCategories || [];
            const subCount = subCats.length;
            // Single option is auto-selected in the UI/API flow; heal stale/draft rows.
            if (subCount === 1 && traineeCategory.category) {
              traineeCategory.subCategory =
                traineeCategory.subCategory || subCats[0];
            }
            traineeCategory.errors.subCategory =
              subCount === 0
                ? undefined
                : subCount === 1
                  ? undefined
                  : !traineeCategory.subCategory
                    ? "Required"
                    : undefined;
            traineeCategory.errors.subLevel =
              !traineeCategory.subLevel &&
              !!traineeCategory?.category?.hasSchoolLevel
                ? "Required"
                : undefined;
            traineeCategory.errors.subject =
              !traineeCategory.subject && traineeCategory?.category?.id === 1
                ? "Required"
                : undefined;

            traineeCategory.errors.numOfTrainees =
              !+traineeCategory.numOfTrainees
                ? "Required"
                : !!+traineeCategory.numOfTrainees &&
                    +totalNumOfTrainees !== +cohot.numOfTrainees &&
                    traineeCategoryIndex + 1 ===
                      targetTrainee.traineeCategories.length &&
                    targetTraineeIndex + 1 === cohot.targetTrainees.length
                  ? "Total trainees must match the cohort total"
                  : undefined;

            for (const errorKey in traineeCategory.errors) {
              if (!!traineeCategory.errors[errorKey]) error.hasError = true;
            }
          },
        );
      });

      totalNumOfTrainees = 0;
      const realVenues = (cohot.venues || []).filter((v) => !isVenuePlaceholder(v));

      // The old form always required at least one venue with trainers; in the wizard we
      // hide the untouched placeholder row, so validate that the user added at least one.
      if (realVenues.length === 0) {
        cohot.errors.venues = "Add at least one location";
        error.hasError = true;
      } else {
        cohot.errors.venues = undefined;
      }

      realVenues.forEach((venue, venueIndex) => {
        totalNumOfTrainees += +venue.numOfTrainees;

        venue.errors.district = !venue.district ? "Required" : undefined;
        venue.errors.sector = !venue.sector ? "Required" : undefined;
        venue.errors.venue = !venue.venue ? "Required" : undefined;

        venue.errors.numOfTrainees = !+venue.numOfTrainees
          ? "Required"
          : !!+venue.numOfTrainees &&
              +totalNumOfTrainees !== +cohot.numOfTrainees &&
              venueIndex + 1 === cohot.venues.length
            ? "Total trainees must match the cohort total"
            : undefined;

        venue.errors.trainers = !venue.trainers?.length
          ? "Trainers are not set"
          : undefined;

        for (const errorKey in venue.errors) {
          if (!!venue.errors[errorKey]) error.hasError = true;
        }
      });
    });

    // Apply errors to materials too (same behavior as old form)
    const tmpTrainingMaterials = [...trainingMaterials];
    tmpTrainingMaterials?.forEach((trainingMaterial) => {
      trainingMaterial.errors.description = !trainingMaterial.description
        ? "Required"
        : undefined;
      trainingMaterial.errors.url = !trainingMaterial.url ? "Required" : undefined;
      for (const errorKey in trainingMaterial.errors) {
        if (!!trainingMaterial.errors[errorKey]) error.hasError = true;
      }
    });

    // Flush computed errors into state
    setTrainingCohorts([...tmpTrainingCohorts]);
    setTrainingMaterials([...tmpTrainingMaterials]);
    setErrors(error);
    if (error.hasError) {
      // Build a step-aware summary so users know what to fix.
      const lines = [];

      const step1Has =
        !!error.titleError ||
        !!error.programError ||
        !!error.subProgramError ||
        !!error.topicsError;

      const step4HasTopLevelNeedsAssessment =
        !!error.descriptionError || !!error.fileError;

      const step4HasMaterials = (tmpTrainingMaterials || []).some(
        (m) => !!m?.errors?.description || !!m?.errors?.url,
      );

      const cohortErrors = [];
      const venueErrors = [];
      const targetLocationErrors = [];

      (tmpTrainingCohorts || []).forEach((c, cohortIdx) => {
        // Cohort header fields
        const ce = c?.errors || {};
        Object.entries(ce).forEach(([k, v]) => {
          if (!v) return;
          // `venues` error is about Step 3 locations (not cohort schedule fields).
          if (k === "venues") {
            venueErrors.push(`Locations: ${v}`);
            return;
          }
          cohortErrors.push(`Cohort #${cohortIdx + 1}: ${k} — ${v}`);
        });

        // Target trainee locations + categories
        (c?.targetTrainees || []).forEach((tt, ttIdx) => {
          const te = tt?.errors || {};
          if (te?.district) targetLocationErrors.push(`Target location #${ttIdx + 1}: district — ${te.district}`);
          if (te?.sector) targetLocationErrors.push(`Target location #${ttIdx + 1}: sector — ${te.sector}`);
          if (te?.school) targetLocationErrors.push(`Target location #${ttIdx + 1}: school — ${te.school}`);

          (tt?.traineeCategories || []).forEach((tc, tcIdx) => {
            const tce = tc?.errors || {};
            const locPrefix = `Target location #${ttIdx + 1}`;
            if (tce?.category)
              targetLocationErrors.push(
                `${locPrefix}, category #${tcIdx + 1}: category — ${tce.category}`,
              );
            if (tce?.subCategory)
              targetLocationErrors.push(
                `${locPrefix}, category #${tcIdx + 1}: sub category — ${tce.subCategory}`,
              );
            if (tce?.subLevel)
              targetLocationErrors.push(
                `${locPrefix}, category #${tcIdx + 1}: school level — ${tce.subLevel}`,
              );
            if (tce?.subject)
              targetLocationErrors.push(
                `${locPrefix}, category #${tcIdx + 1}: subject — ${tce.subject}`,
              );
            if (tce?.numOfTrainees)
              targetLocationErrors.push(
                `${locPrefix}, category #${tcIdx + 1}: trainees — ${tce.numOfTrainees}`,
              );
          });
        });

        // Venues + trainers
        (c?.venues || []).forEach((v, vIdx) => {
          if (isVenuePlaceholder(v)) return;
          const ve = v?.errors || {};
          if (ve?.district) venueErrors.push(`Location #${vIdx + 1}: district — ${ve.district}`);
          if (ve?.sector) venueErrors.push(`Location #${vIdx + 1}: sector — ${ve.sector}`);
          if (ve?.venue) venueErrors.push(`Location #${vIdx + 1}: venue — ${ve.venue}`);
          if (ve?.numOfTrainees) venueErrors.push(`Location #${vIdx + 1}: trainees — ${ve.numOfTrainees}`);
          if (ve?.trainers) venueErrors.push(`Location #${vIdx + 1}: trainers — ${ve.trainers}`);
        });
      });

      // Decide which step to bring into view first.
      let stepToGo = 1;
      if (step1Has) stepToGo = 1;
      else if (venueErrors.length) stepToGo = 3;
      else if (cohortErrors.length || targetLocationErrors.length) stepToGo = 2;
      else if (step4HasMaterials || step4HasTopLevelNeedsAssessment) stepToGo = 4;
      else stepToGo = 1;

      setCurrentStep(stepToGo);
      if (stepToGo === 3 && venueErrors.length) {
        // Help users immediately see the problem rows.
        setLocationFilter("missing");
      }

      lines.push("Please review the form and correct the fields highlighted in red.");
      if (step1Has) {
        const parts = [];
        if (error.titleError) parts.push("title");
        if (error.programError) parts.push("CPD area");
        if (error.subProgramError) parts.push("course");
        if (error.topicsError) parts.push("modules");
        lines.push(`Training info: ${parts.join(", ")} required.`);
      }
      if (cohortErrors.length) lines.push(cohortErrors[0]);
      if (targetLocationErrors.length) lines.push(targetLocationErrors[0]);
      if (venueErrors.length) lines.push(venueErrors[0]);
      if (step4HasMaterials) lines.push("Materials: each row requires description and URL.");
      if (step4HasTopLevelNeedsAssessment) {
        lines.push("Needs assessment: add a description and a PDF (maximum 5 MB).");
      }
      if (lines.length === 1) {
        // Shouldn't happen, but ensures the toast always includes a concrete hint.
        lines.push("Validation failed. Please check each step for missing required fields.");
      }

      // Use a separator that stays readable in a single-line toast.
      showError(lines.join("  •  "));
      return { ok: false };
    }

    return { ok: true, trainingCohorts: tmpTrainingCohorts };
  };

  const onSave = () => {
    const validation = formValidator();
    if (!validation.ok) return;

    const cohortsPayload = validation.trainingCohorts;

    const data = {
        providerProgramId: providerProgram?.id,
        subProgramId: subProgram?.id,
        description: needsAssessment.description,
        title: title,
        topics: selectedTopics.map((topic) => topic.id),
        trainingMaterials: trainingMaterials.map((material) => ({
          description: material.description,
          url: material.url,
        })),
        cohorts: cohortsPayload.map((cohort, cohortIndex) => ({
          cohortNumber: cohortIndex + 1,
          startDate: cohort.startDate,
          endDate: cohort.endDate,
          numOfTrainees: cohort.numOfTrainees || 0,
          numOfHours: cohort.numOfHours || 0,
          budget: cohort.budget || 0,
          targetTraineesLocations: cohort.targetTrainees.map((traineeLocation) => ({
            districtId: traineeLocation.district?.id || null,
            sectorId: traineeLocation.sector?.id || null,
            schoolScopeId: traineeLocation.school?.schoolScopeId || null,
            traineesCategories: traineeLocation.traineeCategories.map(
              (traineeCategory) => ({
                traineeCategoryId: traineeCategory?.category?.id || null,
                traineeSubCategoryId: traineeCategory?.subCategory?.id || null,
                schoolSubLevelId: traineeCategory?.subLevel?.id || null,
                subjectId: traineeCategory?.subject?.id || null,
                numOfTrainees: traineeCategory?.numOfTrainees || 0,
              }),
            ),
          })),
          venues: cohort.venues.map((cohortVenue) => ({
            venueId: cohortVenue.venue?.id,
            numOfTrainees: cohortVenue.numOfTrainees,
            trainers: cohortVenue.trainers.map((trainer) => ({
              trainerId: trainer.id,
              isVenueCoordinator: trainer.isVenueCoordinator || false,
            })),
          })),
        })),
    };

    if (persistedNeedsAssessmentPath && !needsAssessment.file) {
      data.existingNeedsAssessmentPath = persistedNeedsAssessmentPath;
    }

    const formData = new FormData();
    formData.append("formData", JSON.stringify(data));
    if (needsAssessment.file) {
      formData.append("files", needsAssessment.file);
    }

    saveTraining(
      formData,
      () => {
        const draftIdToRemove = activeDraftId;
        if (draftIdToRemove) {
          axios
            .delete(
              `/api/cpd/training-drafts/${draftIdToRemove}?keepUploadedFile=true`,
            )
            .catch(() => {});
        }
        onClose();
      },
      (err) => {
        const apiMsg = getApiErrorMessage(err);
        if (apiMsg && isNeedsAssessmentUploadApiMessage(apiMsg)) {
          setErrors((prev) => ({ ...prev, fileError: apiMsg }));
        }
      },
    );
  };

  const onSaveDraft = async () => {
    try {
      setIsLoading(true);
      const formSnapshot = serializeTrainingFormDraft({
        title,
        providerProgram,
        subProgram,
        selectedTopics,
        needsAssessment,
        trainingCohorts,
        trainingMaterials,
      });

      const payload = { id: activeDraftId, formSnapshot };
      const formData = new FormData();
      formData.append("formData", JSON.stringify(payload));
      if (needsAssessment.file) {
        formData.append("files", needsAssessment.file);
      }

      const { data } = await axios.post("/api/cpd/training-drafts", formData);
      setActiveDraftId(data.id);
      if (data.needsAssessmentFilePath) {
        setPersistedNeedsAssessmentPath(data.needsAssessmentFilePath);
        setNeedsAssessment((na) => ({ ...na, file: null }));
      }
      showSuccess("Draft saved");
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      showError(e);
      const apiMsg = getApiErrorMessage(e);
      if (apiMsg && isNeedsAssessmentUploadApiMessage(apiMsg)) {
        setErrors((prev) => ({ ...prev, fileError: apiMsg }));
      }
    }
  };

  const getApiData = async (url, param, cb = null) => {
    try {
      setIsLoading(true);
      !!cb && cb([]);
      if (!!param) {
        const { data } = await axios.get(`${url.replace(/\/$/, "")}/${param}`);
        !!cb && cb(data);
      }
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  const searchTrainer = async (trainerSearchTerm) => {
    try {
      setIsLoading(true);
      const { data } = await axios.post(`/api/cpd/search-trainer`, {
        trainerSearchTerm,
      });
      setIsLoading(false);
      return data;
    } catch (error) {
      setIsLoading(false);
      showError(error);
    }
  };

  const handleVenueTrainerExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !drawerVenueDraft) return;

    try {
      const buf = await file.arrayBuffer();
      const { terms, error: parseErr } =
        extractTrainerSearchTermsFromVenueTrainerXlsx(buf);
      if (parseErr) {
        showError(parseErr);
        return;
      }
      if (!terms.length) {
        showError("No trainer rows found in the file.");
        return;
      }

      setIsLoading(true);
      const resolved = [];
      const failed = [];

      for (const term of terms) {
        try {
          const { data } = await axios.post(`/api/cpd/search-trainer`, {
            trainerSearchTerm: term,
          });
          if (data?.id) {
            resolved.push({ ...data, isVenueCoordinator: false });
          }
        } catch (err) {
          const msg = getApiErrorMessage(err) || "Not found";
          failed.push({ term, msg });
        }
      }

      setIsLoading(false);
      let newCount = 0;
      setDrawerVenueDraft((prev) => {
        if (!prev) return prev;
        const merged = [...(prev.trainers || [])];
        const ids = new Set(merged.map((t) => t.id));
        for (const t of resolved) {
          if (t.id && !ids.has(t.id)) {
            merged.push(t);
            ids.add(t.id);
            newCount += 1;
          }
        }
        return {
          ...prev,
          trainers: merged,
          errors: { ...(prev.errors || {}), trainers: undefined },
        };
      });

      if (newCount > 0) {
        showSuccess(
          `Added ${newCount} trainer${newCount === 1 ? "" : "s"} from Excel` +
            (failed.length
              ? ` (${failed.length} row${failed.length === 1 ? "" : "s"} not matched).`
              : "."),
        );
      } else if (!failed.length) {
        showSuccess(
          "No new trainers added — everyone in the file was already on this location.",
        );
      }

      if (failed.length) {
        const preview = failed
          .slice(0, 4)
          .map((f) => `${f.term}: ${f.msg}`)
          .join("; ");
        showError(
          failed.length > 4
            ? `${preview}; …and ${failed.length - 4} more`
            : preview || "Some rows could not be matched.",
        );
      }
    } catch (err) {
      setIsLoading(false);
      showError(err);
    }
  };

  const searchSchools = async (q) => {
    const term = (q || "").toString().trim();
    if (term.length < 2) return [];
    try {
      const { data } = await axios.get("/api/posts/schools-search", {
        params: { q: term },
      });
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  };

  const getSubPrograms = async (programId) => {
    try {
      setIsLoading(true);
      setSelectedTopics([]);
      setTopics([]);
      setSubProgram(null);
      setSubPrograms([]);
      const { data } = await axios.get(`/api/cpd/sub-programs/${programId}`);
      setSubPrograms(data);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  const getTopics = async (subProgramId) => {
    try {
      setIsLoading(true);
      setSelectedTopics([]);
      setTopics([]);
      const { data } = await axios.get(`/api/cpd/topics/${subProgramId}`);
      setTopics(data);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!!providerProgram?.programId) getSubPrograms(providerProgram?.programId);
  }, [providerProgram]);

  useEffect(() => {
    if (!!subProgram?.id) getTopics(subProgram?.id);
  }, [subProgram]);

  useEffect(() => {
    if (!showTrainingFormDialog || !resumeFormSnapshot) return;
    const hydrated = hydrateTrainingFormDraft(
      resumeFormSnapshot,
      trainingCohortDefaults,
      trainingMaterialDefaults,
    );
    if (!hydrated) return;

    setTitle(hydrated.title);
    setProviderProgram(hydrated.providerProgram);
    setSubProgram(hydrated.subProgram);
    setSelectedTopics(hydrated.selectedTopics);
    setNeedsAssessment(hydrated.needsAssessment);
    setTrainingMaterials(hydrated.trainingMaterials);
    setTrainingCohorts(hydrated.trainingCohorts);
    setErrors({
      titleError: "",
      programError: "",
      subProgramError: "",
      topicsError: "",
      descriptionError: "",
      fileError: "",
      hasError: false,
    });
    setActiveDraftId(resumeDraftId || null);
    setPersistedNeedsAssessmentPath(resumeNeedsAssessmentFilePath || null);
    setDraftLinkedProviderProgramId(hydrated.providerProgram?.id || null);
    setDraftLinkedSubProgramId(hydrated.subProgram?.id || null);
    setDraftLinkedTopicIds(
      (hydrated.selectedTopics || []).map((t) => t.id).filter(Boolean),
    );
    setCurrentStep(1);
    setActiveCohortIndex(0);
    setLocationSearch("");
    setLocationFilter("all");
    setLocationPage(1);
  }, [
    showTrainingFormDialog,
    resumeFormSnapshot,
    resumeDraftId,
    resumeNeedsAssessmentFilePath,
  ]);

  useEffect(() => {
    if (!draftLinkedProviderProgramId || !providerPrograms?.length) return;
    const match = providerPrograms.find((p) => p.id === draftLinkedProviderProgramId);
    if (match) {
      setProviderProgram(match);
      setDraftLinkedProviderProgramId(null);
    }
  }, [providerPrograms, draftLinkedProviderProgramId]);

  useEffect(() => {
    if (!draftLinkedSubProgramId || !subPrograms?.length) return;
    const match = subPrograms.find((s) => s.id === draftLinkedSubProgramId);
    if (match) {
      setSubProgram(match);
      setDraftLinkedSubProgramId(null);
    }
  }, [subPrograms, draftLinkedSubProgramId]);

  useEffect(() => {
    if (!draftLinkedTopicIds?.length || !topics?.length) return;
    const idSet = new Set(draftLinkedTopicIds);
    setSelectedTopics(topics.filter((t) => idSet.has(t.id)));
    setDraftLinkedTopicIds(null);
  }, [topics, draftLinkedTopicIds]);

  const onClose = () => {
    setTrainingCohorts([cloneCohortWithNewTargetRowKeys(trainingCohortDefaults)]);
    setTitle("");
    setProviderProgram(null);
    setSubProgram(null);
    setSubPrograms([]);
    setTopics([]);
    setSelectedTopics([]);
    setTrainingMaterials([{ ...trainingMaterialDefaults }]);
    setNeedsAssessment({ file: null, description: "" });
    setErrors({
      titleError: "",
      programError: "",
      subProgramError: "",
      topicsError: "",
      descriptionError: "",
      fileError: "",
      hasError: false,
    });
    setActiveDraftId(null);
    setPersistedNeedsAssessmentPath(null);
    setDraftLinkedProviderProgramId(null);
    setDraftLinkedSubProgramId(null);
    setDraftLinkedTopicIds(null);
    setCurrentStep(1);
    setActiveCohortIndex(0);
    setLocationSearch("");
    setLocationFilter("all");
    setLocationPage(1);
    setDrawerOpen(false);
    setDrawerVenueIndex(null);
    setDrawerVenueDraft(null);
    onClearResumeDraft?.();
    setShowTrainingFormDialog(false);
  };

  const activeCohort = trainingCohorts[activeCohortIndex] || trainingCohorts[0];

  const isVenuePlaceholder = (v) => {
    if (!v) return true;
    const hasIdentity = !!v.district || !!v.sector || !!v.venue || !!v.venueType;
    const hasNumbers = !!+v.numOfTrainees;
    const hasTrainers = (v.trainers || []).length > 0;
    return !hasIdentity && !hasNumbers && !hasTrainers;
  };

  const venueTableRows = useMemo(() => {
    const rows = (activeCohort?.venues || [])
      .map((v, idx) => ({
        idx,
        venue: v,
        districtName: v?.district?.name || "",
        sectorName: v?.sector?.name || "",
        schoolName: v?.venue?.name || "",
        trainees: +v?.numOfTrainees || 0,
        trainerCount: v?.trainers?.length || 0,
        placeholder: isVenuePlaceholder(v),
      }))
      // Hide the untouched default placeholder row; it exists to preserve state shape.
      .filter((r) => !r.placeholder);

    const q = (locationSearch || "").trim().toLowerCase();
    let filtered = rows;
    if (q) {
      filtered = filtered.filter((r) => {
        const s = `${r.districtName} ${r.sectorName} ${r.schoolName}`.toLowerCase();
        return s.includes(q);
      });
    }
    if (locationFilter === "has") {
      filtered = filtered.filter((r) => r.trainerCount > 0);
    }
    if (locationFilter === "missing") {
      filtered = filtered.filter((r) => r.trainerCount === 0);
    }
    return filtered;
  }, [activeCohort, locationSearch, locationFilter]);

  const locationsSummary = useMemo(() => {
    const venuesAll = activeCohort?.venues || [];
    const venues = venuesAll.filter((v) => !isVenuePlaceholder(v));
    const total = venues.length;
    const withTrainers = venues.filter((v) => (v?.trainers || []).length > 0).length;
    const missingTrainers = total - withTrainers;
    const totalTrainees = venues.reduce(
      (acc, v) => acc + (+v?.numOfTrainees || 0),
      0,
    );
    return { total, withTrainers, missingTrainers, totalTrainees };
  }, [activeCohort]);

  const openDrawerForIndex = (venueIndex) => {
    const cohortIdx = activeCohortIndex;
    const venue = trainingCohorts[cohortIdx]?.venues?.[venueIndex];
    setDrawerVenueIndex(venueIndex);
    setDrawerVenueDraft(venue ? JSON.parse(JSON.stringify(venue)) : null);
    setDrawerOpen(true);
  };

  const openDrawerForAdd = () => {
    const cohortIdx = activeCohortIndex;
    const cohots = [...trainingCohorts];
    const cohotVenues = [...(cohots[cohortIdx]?.venues || [])];
    const placeholderIdx = cohotVenues.findIndex((v) => isVenuePlaceholder(v));
    if (placeholderIdx >= 0) {
      // Reuse the existing placeholder row.
      setTrainingCohorts(cohots);
      setLocationPage(1);
      openDrawerForIndex(placeholderIdx);
      return;
    }

    cohotVenues.push({ ...trainingCohortDefaults.venues[0] });
    cohots[cohortIdx] = { ...cohots[cohortIdx], venues: cohotVenues };
    setTrainingCohorts(cohots);
    setLocationPage(1);
    openDrawerForIndex(cohotVenues.length - 1);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerVenueIndex(null);
    setDrawerVenueDraft(null);
  };

  const updateDrawerVenueDraft = (patch) => {
    setDrawerVenueDraft((prev) => ({ ...(prev || {}), ...patch }));
  };

  const saveDrawerVenue = () => {
    const cohortIdx = activeCohortIndex;
    const venueIndex = drawerVenueIndex;
    if (venueIndex == null || !drawerVenueDraft) return;

    const next = { ...drawerVenueDraft };
    next.errors = { ...(next.errors || {}) };
    next.errors.district = !next.district ? "Required" : undefined;
    next.errors.sector = !next.sector ? "Required" : undefined;
    next.errors.venue = !next.venue ? "Required" : undefined;
    next.errors.numOfTrainees = !+next.numOfTrainees ? "Required" : undefined;
    next.errors.trainers = !(next.trainers || []).length ? "Trainers are not set" : undefined;

    const hasError = Object.values(next.errors).some(Boolean);
    if (hasError) {
      setDrawerVenueDraft(next);
      showError("Please complete the required location fields.");
      return;
    }

    const cohots = [...trainingCohorts];
    const cohotVenues = [...(cohots[cohortIdx]?.venues || [])];
    cohotVenues[venueIndex] = next;
    cohots[cohortIdx] = { ...cohots[cohortIdx], venues: cohotVenues };
    setTrainingCohorts(cohots);
    showSuccess("Location saved");
    closeDrawer();
  };

  const onDeleteVenue = (venueIndex) => {
    const cohortIdx = activeCohortIndex;
    const cohots = [...trainingCohorts];
    const cohotVenues = [...(cohots[cohortIdx]?.venues || [])];
    if (cohotVenues.length <= 1) return;
    cohotVenues.splice(venueIndex, 1);
    cohots[cohortIdx] = { ...cohots[cohortIdx], venues: cohotVenues };
    setTrainingCohorts(cohots);
  };

  const rowsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(venueTableRows.length / rowsPerPage));
  const pagedRows = venueTableRows.slice(
    (locationPage - 1) * rowsPerPage,
    (locationPage - 1) * rowsPerPage + rowsPerPage,
  );

  useEffect(() => {
    if (locationPage > totalPages) setLocationPage(totalPages);
    if (locationPage < 1) setLocationPage(1);
  }, [totalPages]);

  const renderStep1 = () => (
    <div className="row">
      <div className="col-12 mt-2">
        <TextField
          error={!!errors.titleError}
          helperText={errors.titleError || ""}
          required
          autoFocus
          size="small"
          fullWidth
          name="title"
          label="Training title"
          placeholder="Training title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value || "");
            setErrors({ ...errors, titleError: "" });
          }}
        />
      </div>
      <div className="col-4">
        <Autocomplete
          className="mt-3"
          size="small"
          defaultValue={null}
          value={providerProgram}
          options={providerPrograms}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.program.name}
          renderOption={(optionProps, option) => (
            <Box component="li" {...optionProps}>
              {option.program.name}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!errors.programError}
              helperText={errors.programError || ""}
              name="providerProgram"
              label="CPD area"
              placeholder="CPD area"
              inputProps={{ ...params.inputProps }}
            />
          )}
          onChange={(event, nextProviderProgram) => {
            setProviderProgram(nextProviderProgram || null);
            setErrors({ ...errors, programError: "" });
          }}
        />
      </div>
      <div className="col-4">
        <Autocomplete
          className="mt-3"
          size="small"
          defaultValue={null}
          value={subProgram}
          options={subPrograms}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          renderOption={(optionProps, option) => (
            <Box component="li" {...optionProps}>
              {option.name}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!errors.subProgramError}
              helperText={errors.subProgramError || ""}
              name="subProgram"
              label="Course"
              placeholder="Course"
              inputProps={{ ...params.inputProps }}
            />
          )}
          onChange={(event, nextSubProgram) => {
            setSubProgram(nextSubProgram || null);
            setErrors({ ...errors, subProgramError: "" });
          }}
        />
      </div>
      <div className="col-4">
        <Autocomplete
          multiple
          className="mt-3"
          size="small"
          defaultValue={[]}
          value={selectedTopics}
          options={topics}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          renderOption={(optionProps, option, { selected }) => (
            <ListItem dense {...optionProps}>
              <ListItemAvatar>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              </ListItemAvatar>
              <ListItemText primary={option.name} />
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              error={!!errors.topicsError}
              helperText={errors.topicsError || ""}
              name="topics"
              label="Modules"
              placeholder="Modules"
              inputProps={{ ...params.inputProps }}
            />
          )}
          onChange={(event, selected) => {
            setSelectedTopics(selected || []);
            setErrors({ ...errors, topicsError: "" });
          }}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Typography variant="subtitle2" className="text-primary font-weight-bold">
          Cohorts & schedule
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const cohots = [...trainingCohorts];
            cohots.push(
              cloneCohortWithNewTargetRowKeys(trainingCohortDefaults),
            );
            setTrainingCohorts(cohots);
          }}
        >
          + Add cohort
        </Button>
      </div>

      {trainingCohorts.map((cohort, cohortIndex) => (
        <div
          key={cohortIndex}
          style={{
            background: "#f5f6f7",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Typography variant="subtitle2" className="font-weight-bold">
              Cohort #{cohortIndex + 1}
            </Typography>
            <IconButton
              size="small"
              disabled={trainingCohorts.length <= 1}
              color="error"
              onClick={() => {
                const cohots = [...trainingCohorts];
                cohots.splice(cohortIndex, 1);
                setTrainingCohorts(cohots);
                if (activeCohortIndex >= cohots.length) {
                  setActiveCohortIndex(Math.max(0, cohots.length - 1));
                }
              }}
            >
              <span className="material-icons">close</span>
            </IconButton>
          </div>

          <div className="row">
            <div className="col-6">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  minDate={new Date()}
                  label="Start date"
                  inputFormat="dd/MM/yyyy"
                  value={cohort.startDate || null}
                  onChange={(date) => {
                    const cohots = [...trainingCohorts];
                    cohots[cohortIndex] = {
                      ...cohort,
                      startDate: date,
                      errors: { ...cohort.errors, startDate: undefined },
                    };
                    setTrainingCohorts(cohots);
                  }}
                  renderInput={(params) => (
                    <TextField
                      name="startDate"
                      onKeyDown={(e) => e.preventDefault()}
                      size="small"
                      fullWidth
                      {...params}
                      error={!!cohort.errors?.startDate}
                      helperText={cohort.errors?.startDate || ""}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>
            <div className="col-6">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  disabled={!cohort.startDate}
                  minDate={cohort.startDate || new Date()}
                  label="End date"
                  inputFormat="dd/MM/yyyy"
                  value={cohort.endDate || null}
                  onChange={(date) => {
                    const cohots = [...trainingCohorts];
                    cohots[cohortIndex] = {
                      ...cohort,
                      endDate: date,
                      errors: { ...cohort.errors, endDate: undefined },
                    };
                    setTrainingCohorts(cohots);
                  }}
                  renderInput={(params) => (
                    <TextField
                      name="endDate"
                      onKeyDown={(e) => e.preventDefault()}
                      size="small"
                      fullWidth
                      {...params}
                      error={!!cohort.errors?.endDate}
                      helperText={cohort.errors?.endDate || ""}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>
            <div className="col-4 mt-2">
              <TextField
                error={!!cohort.errors?.numOfHours}
                helperText={cohort.errors?.numOfHours || ""}
                size="small"
                required
                type="number"
                fullWidth
                name="numOfHours"
                label="Total hours"
                placeholder="Total hours"
                variant="outlined"
                value={cohort.numOfHours || 0}
                onChange={(e) => {
                  const cohots = [...trainingCohorts];
                  let value = e.target.value || "";
                  if (value.length > 1) value = value.replace(/^0+/, "");
                  cohots[cohortIndex] = {
                    ...cohort,
                    numOfHours: value,
                    errors: { ...cohort.errors, numOfHours: undefined },
                  };
                  setTrainingCohorts(cohots);
                }}
              />
            </div>
            <div className="col-4 mt-2">
              <TextField
                error={!!cohort.errors?.numOfTrainees}
                helperText={cohort.errors?.numOfTrainees || ""}
                size="small"
                required
                type="number"
                fullWidth
                name="numberOfTrainees"
                label="Trainees"
                placeholder="Trainees"
                variant="outlined"
                value={cohort.numOfTrainees || 0}
                onChange={(e) => {
                  const cohots = [...trainingCohorts];
                  let value = e.target.value || "";
                  if (value.length > 1) value = value.replace(/^0+/, "");
                  cohots[cohortIndex] = {
                    ...cohort,
                    numOfTrainees: value,
                    errors: { ...cohort.errors, numOfTrainees: undefined },
                  };
                  setTrainingCohorts(cohots);
                }}
              />
            </div>
            <div className="col-4 mt-2">
              <NumberFormat
                error={!!cohort.errors?.budget}
                helperText={cohort.errors?.budget || ""}
                fullWidth
                size="small"
                name="budget"
                variant="outlined"
                label="Budget (RWF)"
                placeholder="RWF 0"
                prefix={"RWF "}
                customInput={TextField}
                value={cohort.budget}
                thousandSeparator={true}
                onBlur={() => {
                  if (cohort.budget === "") {
                    const cohots = [...trainingCohorts];
                    cohots[cohortIndex] = { ...cohort, budget: 0 };
                    setTrainingCohorts(cohots);
                  }
                }}
                onKeyDown={() => {
                  if (+cohort.budget === 0) {
                    const cohots = [...trainingCohorts];
                    cohots[cohortIndex] = { ...cohort, budget: "" };
                    setTrainingCohorts(cohots);
                  }
                }}
                onChange={(e) => {
                  const cohots = [...trainingCohorts];
                  let value = e.target.value.split("RWF ") || [];
                  value = !!value[1] ? value[1].replace(/,/g, "") : 0;
                  cohots[cohortIndex] = {
                    ...cohort,
                    budget: value,
                    errors: { ...cohort.errors, budget: undefined },
                  };
                  setTrainingCohorts(cohots);
                }}
              />
            </div>
          </div>

          <Divider className="my-3" />
          <Typography variant="caption" className="text-muted d-block mb-2">
            Target trainees locations (kept identical to existing model)
          </Typography>

          <Button
            variant="text"
            size="small"
            onClick={() => {
              const cohots = [...trainingCohorts];
              const targetTrainees = [...cohots[cohortIndex].targetTrainees];
              const newLocation = JSON.parse(
                JSON.stringify(trainingCohortDefaults.targetTrainees[0]),
              );
              newLocation.rowKey = newTargetTraineeRowKey();
              targetTrainees.unshift(newLocation);
              cohots[cohortIndex] = { ...cohort, targetTrainees };
              setTrainingCohorts(cohots);
            }}
          >
            + Add target location
          </Button>

          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {(cohort.targetTrainees || []).map((traineeTarget, traineeTargetIndex) => (
              <div
                key={traineeTarget.rowKey || `tt-${cohortIndex}-${traineeTargetIndex}`}
                className="border rounded p-2 mb-2 bg-white"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <Typography variant="caption" className="text-muted">
                    Location #{traineeTargetIndex + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={cohort.targetTrainees.length <= 1}
                    color="error"
                    onClick={() => {
                      const cohots = [...trainingCohorts];
                      const targetTrainees = [...cohots[cohortIndex].targetTrainees];
                      targetTrainees.splice(traineeTargetIndex, 1);
                      cohots[cohortIndex] = { ...cohort, targetTrainees };
                      setTrainingCohorts(cohots);
                    }}
                  >
                    <span className="material-icons" style={{ fontSize: 18 }}>
                      remove_circle_outline
                    </span>
                  </IconButton>
                </div>

                <div className="row">
                  <div className="col-12 mb-2">
                    <Autocomplete
                      size="small"
                      value={schoolLookupByRowKey[traineeTarget.rowKey || traineeTargetIndex]?.value || null}
                      options={
                        schoolLookupByRowKey[traineeTarget.rowKey || traineeTargetIndex]?.options ||
                        []
                      }
                      loading={
                        !!schoolLookupByRowKey[traineeTarget.rowKey || traineeTargetIndex]
                          ?.loading
                      }
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) =>
                        option?.name
                          ? `${option.name}${
                              option.sdmsSchoolCode
                                ? ` (${option.sdmsSchoolCode})`
                                : ""
                            }`
                          : ""
                      }
                      renderOption={(optionProps, option) => (
                        <Box component="li" {...optionProps}>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 600 }}>
                              {option.name}
                              {option.sdmsSchoolCode ? ` (${option.sdmsSchoolCode})` : ""}
                            </span>
                            <span style={{ fontSize: 12, color: "#6c757d" }}>
                              {option.districtName} · {option.sectorName}
                            </span>
                          </div>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search school (name or code)"
                          placeholder="Type at least 2 characters…"
                        />
                      )}
                      onInputChange={(e, inputValue) => {
                        const rk = traineeTarget.rowKey || traineeTargetIndex;
                        // debounce per-rowKey
                        const timers = schoolLookupTimersRef.current;
                        if (timers[rk]) clearTimeout(timers[rk]);
                        setSchoolLookupByRowKey((prev) => ({
                          ...prev,
                          [rk]: {
                            ...(prev[rk] || {}),
                            input: inputValue,
                            loading: (inputValue || "").trim().length >= 2,
                          },
                        }));
                        timers[rk] = setTimeout(async () => {
                          const results = await searchSchools(inputValue);
                          setSchoolLookupByRowKey((prev) => ({
                            ...prev,
                            [rk]: {
                              ...(prev[rk] || {}),
                              options: results,
                              loading: false,
                            },
                          }));
                        }, 300);
                      }}
                      onChange={(e, selectedSchool) => {
                        if (!selectedSchool) return;
                        const rk = traineeTarget.rowKey || traineeTargetIndex;

                        // Build objects that match existing dropdown option shapes.
                        const districtObj =
                          districts?.find((d) => d.id === selectedSchool.districtId) || {
                            id: selectedSchool.districtId,
                            name: selectedSchool.districtName,
                          };
                        const sectorObj = {
                          id: selectedSchool.sectorId,
                          name: selectedSchool.sectorName,
                        };

                        const schoolObj = {
                          id: selectedSchool.id,
                          name: selectedSchool.name,
                          schoolScopeId: selectedSchool.schoolScopeId,
                        };

                        const cohots = [...trainingCohorts];
                        const targetTrainees = [...cohots[cohortIndex].targetTrainees];

                        // 1) set district and load sectors
                        getApiData("/api/posts/sectors", districtObj?.id, (sectors) => {
                          // 2) set sector and load schools
                          getApiData("/api/posts/schools", sectorObj?.id, (schools) => {
                            targetTrainees[traineeTargetIndex] = {
                              ...traineeTarget,
                              district: districtObj,
                              sectors: sectors || [],
                              sector: sectorObj,
                              schools: schools || [],
                              school: schoolObj,
                              traineeCategories: [
                                ...trainingCohortDefaults.targetTrainees[0].traineeCategories,
                              ],
                              errors: {
                                ...(traineeTarget.errors || {}),
                                district: undefined,
                                sector: undefined,
                                school: undefined,
                              },
                            };
                            cohots[cohortIndex] = { ...cohort, targetTrainees };
                            setTrainingCohorts(cohots);
                            setSchoolLookupByRowKey((prev) => ({
                              ...prev,
                              [rk]: { ...(prev[rk] || {}), value: selectedSchool },
                            }));
                          });
                        });
                      }}
                    />
                  </div>
                  <div className="col-4">
                    <Autocomplete
                      size="small"
                      defaultValue={null}
                      value={traineeTarget.school}
                      options={[
                        { id: null, name: "All" },
                        ...(traineeTarget.schools || []),
                      ]}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name}
                      renderOption={(optionProps, option) => (
                        <Box component="li" {...optionProps}>
                          {option.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          {...params}
                          error={!!traineeTarget.errors?.school}
                          helperText={traineeTarget.errors?.school || ""}
                          name="school"
                          label="School"
                          placeholder="School"
                          inputProps={{ ...params.inputProps }}
                        />
                      )}
                      onChange={(event, school) => {
                        const cohots = [...trainingCohorts];
                        const targetTrainees = [...cohots[cohortIndex].targetTrainees];
                        targetTrainees[traineeTargetIndex] = {
                          ...traineeTarget,
                          school,
                          traineeCategories: [
                            ...trainingCohortDefaults.targetTrainees[0].traineeCategories,
                          ],
                          errors: { ...traineeTarget.errors, school: undefined },
                        };
                        cohots[cohortIndex] = { ...cohort, targetTrainees };
                        setTrainingCohorts(cohots);
                      }}
                    />
                  </div>
                  <div className="col-4">
                    <Autocomplete
                      size="small"
                      defaultValue={null}
                      value={traineeTarget.district}
                      options={districts}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name}
                      renderOption={(optionProps, option) => (
                        <Box component="li" {...optionProps}>
                          {option.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          {...params}
                          error={!!traineeTarget.errors?.district}
                          helperText={traineeTarget.errors?.district || ""}
                          name="district"
                          label="District"
                          placeholder="District"
                          inputProps={{ ...params.inputProps }}
                        />
                      )}
                      onChange={(event, district) => {
                        const cohots = [...trainingCohorts];
                        const targetTrainees = [...cohots[cohortIndex].targetTrainees];
                        getApiData("/api/posts/sectors", district?.id, (data) => {
                          targetTrainees[traineeTargetIndex] = {
                            ...traineeTarget,
                            district,
                            sectors: data,
                            sector: null,
                            schools: [],
                            school: null,
                            traineeCategories: [
                              ...trainingCohortDefaults.targetTrainees[0].traineeCategories,
                            ],
                            errors: { ...traineeTarget.errors, district: undefined },
                          };
                          cohots[cohortIndex] = { ...cohort, targetTrainees };
                          setTrainingCohorts(cohots);
                        });
                      }}
                    />
                  </div>
                  <div className="col-4">
                    <Autocomplete
                      size="small"
                      defaultValue={null}
                      value={traineeTarget.sector}
                      options={[
                        { id: null, name: "All" },
                        ...(traineeTarget.sectors || []),
                      ]}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name}
                      renderOption={(optionProps, option) => (
                        <Box component="li" {...optionProps}>
                          {option.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          variant="outlined"
                          {...params}
                          error={!!traineeTarget.errors?.sector}
                          helperText={traineeTarget.errors?.sector || ""}
                          name="sector"
                          label="Sector"
                          placeholder="Sector"
                          inputProps={{ ...params.inputProps }}
                        />
                      )}
                      onChange={(event, sector) => {
                        const cohots = [...trainingCohorts];
                        const targetTrainees = [...cohots[cohortIndex].targetTrainees];
                        getApiData("/api/posts/schools", sector?.id, (data) => {
                          targetTrainees[traineeTargetIndex] = {
                            ...traineeTarget,
                            sector,
                            schools: data,
                            school: !!sector && !sector.id ? { id: null, name: "All" } : null,
                            traineeCategories: [
                              ...trainingCohortDefaults.targetTrainees[0].traineeCategories,
                            ],
                            errors: {
                              ...traineeTarget.errors,
                              sector: undefined,
                              school:
                                !!sector && !sector.id ? undefined : traineeTarget?.errors?.school,
                            },
                          };
                          cohots[cohortIndex] = { ...cohort, targetTrainees };
                          setTrainingCohorts(cohots);
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="mt-2 border rounded p-2 bg-light">
                  <div className="d-flex align-items-center justify-content-between">
                    <Typography variant="caption" className="text-muted">
                      Trainee categories
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        const cohots = [...trainingCohorts];
                        const targetTrainees = [...cohots[cohortIndex].targetTrainees];
                        const tmpTraineeCategories = [
                          ...(traineeTarget.traineeCategories || []),
                        ];
                        tmpTraineeCategories.push({
                          ...trainingCohortDefaults.targetTrainees[0].traineeCategories[0],
                        });
                        targetTrainees[traineeTargetIndex] = {
                          ...traineeTarget,
                          traineeCategories: tmpTraineeCategories,
                        };
                        cohots[cohortIndex] = { ...cohort, targetTrainees };
                        setTrainingCohorts(cohots);
                      }}
                    >
                      + Add category
                    </Button>
                  </div>

                  {(traineeTarget.traineeCategories || []).map(
                    (traineeCategory, traineeCategoryIndex) => (
                      <div
                        key={traineeCategoryIndex}
                        className="border rounded bg-white p-2 mb-2"
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <Typography variant="caption" className="text-muted">
                            Category #{traineeCategoryIndex + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={(traineeTarget.traineeCategories || []).length <= 1}
                            color="error"
                            onClick={() => {
                              const cohots = [...trainingCohorts];
                              const targetTrainees = [
                                ...cohots[cohortIndex].targetTrainees,
                              ];
                              const tmpTraineeCategories = [
                                ...traineeTarget.traineeCategories,
                              ];
                              tmpTraineeCategories.splice(traineeCategoryIndex, 1);
                              targetTrainees[traineeTargetIndex] = {
                                ...traineeTarget,
                                traineeCategories: tmpTraineeCategories,
                              };
                              cohots[cohortIndex] = { ...cohort, targetTrainees };
                              setTrainingCohorts(cohots);
                            }}
                          >
                            <span className="material-icons" style={{ fontSize: 18 }}>
                              close
                            </span>
                          </IconButton>
                        </div>

                        <div className="row">
                          <div className="col-4">
                            <Autocomplete
                              size="small"
                              defaultValue={null}
                              value={traineeCategory.category}
                              options={traineeCategories || []}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                              getOptionLabel={(option) => option.name}
                              renderOption={(optionProps, option) => (
                                <Box component="li" {...optionProps}>
                                  {option.name}
                                </Box>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  variant="outlined"
                                  {...params}
                                  error={!!traineeCategory.errors?.category}
                                  helperText={traineeCategory.errors?.category || ""}
                                  name="traineeCategory"
                                  label="Category"
                                  placeholder="Category"
                                  inputProps={{ ...params.inputProps }}
                                />
                              )}
                              onChange={(event, category) => {
                                const cohots = [...trainingCohorts];
                                const targetTrainees = [
                                  ...cohots[cohortIndex].targetTrainees,
                                ];
                                const tmpTraineeCategories = [
                                  ...traineeTarget.traineeCategories,
                                ];

                                getApiData(
                                  "/api/cpd/trainee-sub-categories",
                                  category?.id,
                                  (data) => {
                                    tmpTraineeCategories[traineeCategoryIndex] = {
                                      ...traineeCategory,
                                      category: category || null,
                                      subCategories: data,
                                      subCategory: data.length === 1 ? data[0] : null,
                                      subLevel: null,
                                      subject: null,
                                      subjects: [],
                                      numOfTrainees: "",
                                      errors: {
                                        ...(traineeCategory.errors || {}),
                                        category: undefined,
                                      },
                                    };

                                    targetTrainees[traineeTargetIndex] = {
                                      ...traineeTarget,
                                      traineeCategories: tmpTraineeCategories,
                                    };
                                    cohots[cohortIndex] = { ...cohort, targetTrainees };
                                    setTrainingCohorts(cohots);
                                  },
                                );
                              }}
                            />
                          </div>

                          {(traineeCategory.subCategories || []).length >= 1 && (
                            <div className="col-4">
                              <Autocomplete
                                disabled={
                                  !traineeCategory.category ||
                                  (traineeCategory.subCategories || [])
                                    .length === 1
                                }
                                size="small"
                                defaultValue={null}
                                value={
                                  (traineeCategory.subCategories || [])
                                    .length === 1
                                    ? traineeCategory.subCategory ||
                                      (traineeCategory.subCategories ||
                                        [])[0]
                                    : traineeCategory.subCategory
                                }
                                options={traineeCategory.subCategories || []}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                getOptionLabel={(option) => option.name}
                                renderOption={(optionProps, option) => (
                                  <Box component="li" {...optionProps}>
                                    {option.name}
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    variant="outlined"
                                    {...params}
                                    error={!!traineeCategory.errors?.subCategory}
                                    helperText={
                                      traineeCategory.errors?.subCategory || ""
                                    }
                                    name="subCategory"
                                    label="Sub category"
                                    placeholder="Sub category"
                                    inputProps={{ ...params.inputProps }}
                                  />
                                )}
                                onChange={(event, subCategory) => {
                                  const cohots = [...trainingCohorts];
                                  const targetTrainees = [
                                    ...cohots[cohortIndex].targetTrainees,
                                  ];
                                  const tmpTraineeCategories = [
                                    ...traineeTarget.traineeCategories,
                                  ];
                                  tmpTraineeCategories[traineeCategoryIndex] = {
                                    ...traineeCategory,
                                    subCategory: subCategory || null,
                                    subLevel: null,
                                    subject: null,
                                    subjects: [],
                                    numOfTrainees: "",
                                    errors: {
                                      ...(traineeCategory.errors || {}),
                                      subCategory: undefined,
                                    },
                                  };
                                  targetTrainees[traineeTargetIndex] = {
                                    ...traineeTarget,
                                    traineeCategories: tmpTraineeCategories,
                                  };
                                  cohots[cohortIndex] = { ...cohort, targetTrainees };
                                  setTrainingCohorts(cohots);
                                }}
                              />
                            </div>
                          )}

                          {(!traineeCategory?.category ||
                            !!traineeCategory?.category?.hasSchoolLevel) && (
                            <div className="col-4">
                              <Autocomplete
                                disabled={!traineeCategory.subCategory}
                                size="small"
                                defaultValue={null}
                                value={traineeCategory.subLevel}
                                options={schoolSubLevels || []}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                getOptionLabel={(option) => option.name}
                                renderOption={(optionProps, option) => (
                                  <Box component="li" {...optionProps}>
                                    {option.name}
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    variant="outlined"
                                    {...params}
                                    error={!!traineeCategory.errors?.subLevel}
                                    helperText={traineeCategory.errors?.subLevel || ""}
                                    name="schoolSubLevel"
                                    label="School level"
                                    placeholder="School level"
                                    inputProps={{ ...params.inputProps }}
                                  />
                                )}
                                onChange={(event, subLevel) => {
                                  const cohots = [...trainingCohorts];
                                  const targetTrainees = [
                                    ...cohots[cohortIndex].targetTrainees,
                                  ];
                                  const tmpTraineeCategories = [
                                    ...traineeTarget.traineeCategories,
                                  ];

                                  getApiData(
                                    "/api/posts/school-sub-level-subjects",
                                    subLevel?.id,
                                    (data) => {
                                      tmpTraineeCategories[traineeCategoryIndex] = {
                                        ...traineeCategory,
                                        subLevel: subLevel || null,
                                        subjects: data,
                                        subject:
                                          subLevel?.id === 1
                                            ? { id: null, name: "All" }
                                            : null,
                                        numOfTrainees: "",
                                        errors: {
                                          ...(traineeCategory.errors || {}),
                                          subLevel: undefined,
                                        },
                                      };
                                      targetTrainees[traineeTargetIndex] = {
                                        ...traineeTarget,
                                        traineeCategories: tmpTraineeCategories,
                                      };
                                      cohots[cohortIndex] = { ...cohort, targetTrainees };
                                      setTrainingCohorts(cohots);
                                    },
                                  );
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <div className="row mt-2">
                          {(!traineeCategory?.category ||
                            (traineeCategory?.category?.id === 1 &&
                              traineeCategory?.subLevel?.id !== 1)) && (
                            <div className="col-6">
                              <Autocomplete
                                disabled={!traineeCategory.subLevel}
                                size="small"
                                defaultValue={null}
                                value={traineeCategory.subject}
                                options={[
                                  { id: null, name: "All" },
                                  ...(traineeCategory.subjects || []),
                                ]}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                getOptionLabel={(option) => option.name}
                                renderOption={(optionProps, option) => (
                                  <Box component="li" {...optionProps}>
                                    {option.name}
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    variant="outlined"
                                    {...params}
                                    error={!!traineeCategory.errors?.subject}
                                    helperText={traineeCategory.errors?.subject || ""}
                                    name="subject"
                                    label="Subject"
                                    placeholder="Subject"
                                    inputProps={{ ...params.inputProps }}
                                  />
                                )}
                                onChange={(event, subject) => {
                                  const cohots = [...trainingCohorts];
                                  const targetTrainees = [
                                    ...cohots[cohortIndex].targetTrainees,
                                  ];
                                  const tmpTraineeCategories = [
                                    ...traineeTarget.traineeCategories,
                                  ];
                                  tmpTraineeCategories[traineeCategoryIndex] = {
                                    ...traineeCategory,
                                    subject: subject || null,
                                    numOfTrainees: "",
                                    errors: {
                                      ...(traineeCategory.errors || {}),
                                      subject: undefined,
                                    },
                                  };
                                  targetTrainees[traineeTargetIndex] = {
                                    ...traineeTarget,
                                    traineeCategories: tmpTraineeCategories,
                                  };
                                  cohots[cohortIndex] = { ...cohort, targetTrainees };
                                  setTrainingCohorts(cohots);
                                }}
                              />
                            </div>
                          )}

                          <div className="col-6">
                            <TextField
                              error={!!traineeCategory.errors?.numOfTrainees}
                              helperText={traineeCategory.errors?.numOfTrainees || ""}
                              disabled={
                                !traineeCategory?.category ||
                                (!!traineeCategory?.category?.hasSchoolLevel &&
                                  !traineeCategory?.subLevel) ||
                                (traineeCategory?.category?.id === 1 &&
                                  !traineeCategory?.subject) ||
                                ((traineeCategory.subCategories || []).length >
                                  1 &&
                                  !traineeCategory?.subCategory)
                              }
                              variant="outlined"
                              size="small"
                              required
                              type="number"
                              fullWidth
                              name="numberOfTrainees"
                              label="Number of trainees"
                              placeholder="Number of trainees"
                              value={traineeCategory.numOfTrainees || 0}
                              onChange={(e) => {
                                const cohots = [...trainingCohorts];
                                const targetTrainees = [
                                  ...cohots[cohortIndex].targetTrainees,
                                ];
                                const tmpTraineeCategories = [
                                  ...traineeTarget.traineeCategories,
                                ];
                                let value = e.target.value || "";
                                if (value.length > 1) value = value.replace(/^0+/, "");
                                tmpTraineeCategories[traineeCategoryIndex] = {
                                  ...traineeCategory,
                                  numOfTrainees: value,
                                  errors: {
                                    ...(traineeCategory.errors || {}),
                                    numOfTrainees: undefined,
                                  },
                                };
                                targetTrainees[traineeTargetIndex] = {
                                  ...traineeTarget,
                                  traineeCategories: tmpTraineeCategories,
                                };
                                cohots[cohortIndex] = { ...cohort, targetTrainees };
                                setTrainingCohorts(cohots);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep3 = () => (
    <div style={{ position: "relative" }}>
      {(() => {
        const targetLocations = activeCohort?.targetTrainees || [];
        const hasTargets = targetLocations.some((t) => !!t?.district && !!t?.sector);
        const hasVenues = (activeCohort?.venues || []).some((v) => !isVenuePlaceholder(v));

        const addFromTargets = async () => {
          try {
            setIsLoading(true);
            const cohortIdx = activeCohortIndex;
            const cohots = [...trainingCohorts];
            const cohort = cohots[cohortIdx];
            const existing = [...(cohort.venues || [])].filter(
              (v) => !isVenuePlaceholder(v),
            );

            const toAdd = (cohort.targetTrainees || [])
              .filter((t) => !!t?.district && !!t?.sector)
              .map((t) => ({
                ...trainingCohortDefaults.venues[0],
                district: t.district,
                sector: t.sector,
              }));

            // Keep the original placeholder row (if any) to preserve shape,
            // then append the derived rows.
            const placeholder = (cohort.venues || []).find((v) => isVenuePlaceholder(v));
            const nextVenues = [
              ...(placeholder ? [placeholder] : []),
              ...existing,
              ...toAdd,
            ];

            cohots[cohortIdx] = { ...cohort, venues: nextVenues };
            setTrainingCohorts(cohots);
            setLocationPage(1);
            showSuccess("Locations added. Open a row to assign trainers.");
          } finally {
            setIsLoading(false);
          }
        };

        if (hasVenues || !hasTargets) return null;

        return (
          <div className="mb-3 p-2 rounded" style={{ background: "#E6F1FB" }}>
            <p
              className="mb-0"
              style={{
                color: "#c62828",
                fontWeight: 600,
                lineHeight: 1.45,
              }}
            >
              You added target trainee locations in step 2. Step 3 manages training
              venues + trainers. You can create venue rows from your step 2 locations, then
              open each row to select the venue and assign trainers.
            </p>
            <div className="mt-2">
              <Button
                size="small"
                variant="contained"
                disabled={loading || isLoading}
                onClick={addFromTargets}
              >
                Add locations from cohort targets
              </Button>
            </div>
          </div>
        );
      })()}

      <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 12 }}>
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
          <TextField
            size="small"
            label="Search district, sector, school…"
            value={locationSearch}
            onChange={(e) => {
              setLocationSearch(e.target.value || "");
              setLocationPage(1);
            }}
            style={{ width: 320 }}
          />
          <TextField
            select
            size="small"
            label="Filter"
            value={locationFilter}
            onChange={(e) => {
              setLocationFilter(e.target.value);
              setLocationPage(1);
            }}
            style={{ width: 180 }}
          >
            <MenuItem value="all">All locations</MenuItem>
            <MenuItem value="has">Has trainers</MenuItem>
            <MenuItem value="missing">Missing trainers</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Cohort"
            value={activeCohortIndex}
            onChange={(e) => {
              setActiveCohortIndex(+e.target.value);
              setLocationPage(1);
            }}
            style={{ width: 140 }}
          >
            {trainingCohorts.map((c, idx) => (
              <MenuItem key={idx} value={idx}>
                Cohort {idx + 1}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <Button variant="contained" onClick={openDrawerForAdd}>
          + Add location
        </Button>
      </div>

      <div
        className="mt-3 p-2 rounded"
        style={{
          background: "#f8f9fa",
          border: "1px solid #e0e0e0",
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div className="px-2 py-1 border rounded bg-white" style={{ minWidth: 140 }}>
          <div style={{ fontSize: 12, color: "#6c757d" }}>Total locations</div>
          <div style={{ fontWeight: 700 }}>{locationsSummary.total}</div>
        </div>
        <div className="px-2 py-1 border rounded bg-white" style={{ minWidth: 160 }}>
          <div style={{ fontSize: 12, color: "#6c757d" }}>Trainers assigned</div>
          <div style={{ fontWeight: 700 }}>{locationsSummary.withTrainers}</div>
        </div>
        <div
          className="px-2 py-1 border rounded"
          style={{
            minWidth: 170,
            background: locationsSummary.missingTrainers > 0 ? "#FAEEDA" : "#fff",
            color: locationsSummary.missingTrainers > 0 ? "#854F0B" : "inherit",
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.85 }}>Missing trainers</div>
          <div style={{ fontWeight: 700 }}>{locationsSummary.missingTrainers}</div>
        </div>
        <div className="px-2 py-1 border rounded bg-white" style={{ minWidth: 170 }}>
          <div style={{ fontSize: 12, color: "#6c757d" }}>Total trainees</div>
          <div style={{ fontWeight: 700 }}>{locationsSummary.totalTrainees}</div>
        </div>
      </div>

      <div className="mt-3">
        <table className="table table-striped table-hover">
          <thead>
            <tr style={{ backgroundColor: "#f0f2f5" }}>
              <th style={{ width: 48 }}>#</th>
              <th>District</th>
              <th>Sector / School</th>
              <th style={{ width: 110 }}>Trainees</th>
              <th style={{ width: 220 }}>Trainers</th>
              <th style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((r, i) => {
              const v = r.venue;
              const firstTrainer = (v.trainers || [])[0];
              const extra = Math.max(0, (v.trainers || []).length - 1);
              return (
                <tr
                  key={`${r.idx}-${r.districtName}-${r.schoolName}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => openDrawerForIndex(r.idx)}
                >
                  <td>{(locationPage - 1) * rowsPerPage + i + 1}</td>
                  <td>{r.districtName || "-"}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.sectorName || "-"}</div>
                    <div style={{ fontSize: 12, color: "#6c757d" }}>
                      {r.schoolName || "-"}
                    </div>
                  </td>
                  <td>{r.trainees || 0}</td>
                  <td>
                    {(v.trainers || []).length === 0 ? (
                      <span
                        className="px-2 py-1"
                        style={{
                          borderRadius: 999,
                          background: "#e9ecef",
                          color: "#6c757d",
                          fontSize: 12,
                        }}
                      >
                        None
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                        <span
                          className="px-2 py-1"
                          style={{
                            borderRadius: 20,
                            background: "#E6F1FB",
                            color: "#185FA5",
                            fontSize: 12,
                          }}
                        >
                          {firstTrainer?.lastName} {firstTrainer?.firstName}
                        </span>
                        {extra > 0 && (
                          <span
                            className="px-2 py-1"
                            style={{
                              borderRadius: 20,
                              background: "#FAEEDA",
                              color: "#854F0B",
                              fontSize: 12,
                            }}
                          >
                            +{extra} more
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      disabled={(activeCohort?.venues || []).length <= 1}
                      onClick={() => onDeleteVenue(r.idx)}
                    >
                      <span className="material-icons">close</span>
                    </IconButton>
                  </td>
                </tr>
              );
            })}
            {pagedRows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  {locationsSummary.total > 0 &&
                  (locationFilter === "has" || locationFilter === "missing") ? (
                    <>
                      No rows match the current filter.
                      <div className="mt-2">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setLocationFilter("all")}
                        >
                          Show all locations
                        </Button>
                      </div>
                    </>
                  ) : (
                    "No venue locations yet. Click “+ Add location” to create one."
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="d-flex justify-content-end align-items-center" style={{ gap: 8 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={locationPage <= 1}
            onClick={() => setLocationPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <div style={{ fontSize: 13 }}>
            {locationPage} / {totalPages}
          </div>
          <Button
            size="small"
            variant="outlined"
            disabled={locationPage >= totalPages}
            onClick={() => setLocationPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Pop-out editor (separate overlay above the wizard modal) */}
      <MuiDialog
        open={drawerOpen}
        onClose={closeDrawer}
        fullWidth
        maxWidth="xs"
        sx={{
          zIndex: (theme) => (theme?.zIndex?.modal || 1300) + 50,
        }}
        PaperProps={{
          style: {
            position: "fixed",
            right: 12,
            margin: 0,
            height: "calc(100% - 24px)",
            width: 320,
            borderRadius: 8,
            overflow: "hidden",
          },
        }}
        BackdropProps={{
          style: { background: "rgba(0,0,0,0.25)" },
        }}
      >
        <MuiDialogTitle>
          <div className="d-flex align-items-center justify-content-between">
            <Typography variant="subtitle2" className="font-weight-bold">
              {drawerVenueIndex == null
                ? "Location"
                : `Location #${drawerVenueIndex + 1}`}
            </Typography>
            <IconButton onClick={closeDrawer} size="small">
              <span className="material-icons">close</span>
            </IconButton>
          </div>
        </MuiDialogTitle>
        <MuiDialogContent dividers>
          {drawerVenueDraft && (
            <div>
            <Autocomplete
              className="mb-2"
              size="small"
              disablePortal
              value={drawerVenueDraft.venueType}
              options={[...(venueTypes || []), { id: null, name: "Other" }]}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField {...params} label="Venue type" placeholder="Venue type" />
              )}
              onChange={(e, venueType) => {
                updateDrawerVenueDraft({ venueType, venue: null, numOfTrainees: "" });
              }}
            />

            <Autocomplete
              className="mb-2"
              size="small"
              disablePortal
              value={drawerVenueDraft.district}
              options={districts}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="District"
                  placeholder="District"
                  error={!!drawerVenueDraft.errors?.district}
                  helperText={drawerVenueDraft.errors?.district || ""}
                />
              )}
              onChange={(e, district) => {
                getApiData("/api/posts/sectors", district?.id, (data) => {
                  updateDrawerVenueDraft({
                    district,
                    sectors: data,
                    sector: null,
                    venues: [],
                    venue: null,
                    numOfTrainees: "",
                    errors: { ...(drawerVenueDraft.errors || {}), district: undefined },
                  });
                });
              }}
            />

            <Autocomplete
              className="mb-2"
              size="small"
              disablePortal
              value={drawerVenueDraft.sector}
              options={drawerVenueDraft.sectors || []}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sector"
                  placeholder="Sector"
                  error={!!drawerVenueDraft.errors?.sector}
                  helperText={drawerVenueDraft.errors?.sector || ""}
                />
              )}
              onChange={(e, sector) => {
                getApiData("/api/cpd/venues", sector?.id, (data) => {
                  updateDrawerVenueDraft({
                    sector,
                    venues: data,
                    venue: null,
                    numOfTrainees: "",
                    errors: { ...(drawerVenueDraft.errors || {}), sector: undefined },
                  });
                });
              }}
            />

            <Autocomplete
              className="mb-2"
              size="small"
              disablePortal
              value={drawerVenueDraft.venue}
              options={(drawerVenueDraft.venues || []).filter(
                ({ venueTypeId }) =>
                  venueTypeId === (drawerVenueDraft.venueType?.id || null),
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="School / Venue"
                  placeholder="School / Venue"
                  error={!!drawerVenueDraft.errors?.venue}
                  helperText={drawerVenueDraft.errors?.venue || ""}
                />
              )}
              onChange={(e, venue) => {
                updateDrawerVenueDraft({
                  venue,
                  numOfTrainees: "",
                  errors: { ...(drawerVenueDraft.errors || {}), venue: undefined },
                });
              }}
            />

            <TextField
              className="mb-2"
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              label="Number of trainees"
              value={drawerVenueDraft.numOfTrainees || 0}
              error={!!drawerVenueDraft.errors?.numOfTrainees}
              helperText={drawerVenueDraft.errors?.numOfTrainees || ""}
              onChange={(e) => {
                let value = e.target.value || "";
                if (value.length > 1) value = value.replace(/^0+/, "");
                updateDrawerVenueDraft({
                  numOfTrainees: value,
                  errors: { ...(drawerVenueDraft.errors || {}), numOfTrainees: undefined },
                });
              }}
            />

            <Divider className="my-2" />
            <Typography variant="subtitle2" className="font-weight-bold mb-1">
              Trainers
            </Typography>

            <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                ref={venueTrainerExcelInputRef}
                className="d-none"
                onChange={handleVenueTrainerExcelUpload}
              />
              <Tooltip title="Download Excel template for this location">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => downloadVenueTrainersTemplate()}
                  disabled={!!isLoading}
                >
                  <span className="material-icons mr-1" style={{ fontSize: 18 }}>
                    description
                  </span>
                  Template
                </Button>
              </Tooltip>
              <Tooltip title="Upload a filled .xlsx to add trainers in bulk">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => venueTrainerExcelInputRef.current?.click()}
                  disabled={!!isLoading}
                >
                  <span className="material-icons mr-1" style={{ fontSize: 18 }}>
                    upload_file
                  </span>
                  Import Excel
                </Button>
              </Tooltip>
            </div>

            <div className="d-flex" style={{ gap: 8 }}>
              <TextField
                size="small"
                fullWidth
                label="Search by NID, phone, or email…"
                value={drawerVenueDraft.trainerSearchTerm || ""}
                error={!!drawerVenueDraft.errors?.trainers}
                helperText={drawerVenueDraft.errors?.trainers || ""}
                onChange={(e) =>
                  updateDrawerVenueDraft({ trainerSearchTerm: e.target.value || "" })
                }
              />
              <Button
                variant="contained"
                disabled={!drawerVenueDraft.trainerSearchTerm || !!isLoading}
                onClick={async () => {
                  const data = await searchTrainer(drawerVenueDraft.trainerSearchTerm);
                  const trainers = [...(drawerVenueDraft.trainers || [])];
                  if (!!data && !trainers.find(({ id }) => id === data.id)) {
                    trainers.push({ ...data, isVenueCoordinator: false });
                  }
                  updateDrawerVenueDraft({
                    trainers,
                    trainerSearchTerm: "",
                    errors: { ...(drawerVenueDraft.errors || {}), trainers: undefined },
                  });
                }}
              >
                Add
              </Button>
            </div>

            <div className="mt-2" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(drawerVenueDraft.trainers || []).map((t) => (
                <span
                  key={t.id}
                  style={{
                    background: "#E6F1FB",
                    color: "#185FA5",
                    borderRadius: 20,
                    padding: "2px 8px",
                    fontSize: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {t.lastName} {t.firstName}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => {
                      const trainers = [...(drawerVenueDraft.trainers || [])].filter(
                        (x) => x.id !== t.id,
                      );
                      updateDrawerVenueDraft({ trainers });
                    }}
                    style={{ lineHeight: 1 }}
                  >
                    <span className="material-icons" style={{ fontSize: 16 }}>
                      close
                    </span>
                  </button>
                </span>
              ))}
            </div>

            {(() => {
              const venues = activeCohort?.venues || [];
              const options = venues
                .map((v, idx) => ({
                  idx,
                  label:
                    v?.venue?.name ||
                    `${v?.district?.name || "Location"} ${idx + 1}`,
                  has: (v?.trainers || []).length > 0,
                  trainers: v?.trainers || [],
                }))
                .filter((o) => o.has && o.idx !== drawerVenueIndex);

              if (options.length === 0) return null;
              return (
                <div className="mt-2">
                  <Typography variant="caption" className="text-muted d-block">
                    Copy trainers from:
                  </Typography>
                  <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                    {options.slice(0, 5).map((o) => (
                      <Button
                        key={o.idx}
                        size="small"
                        variant="text"
                        onClick={() => {
                          updateDrawerVenueDraft({
                            trainers: (o.trainers || []).map((t) => ({ ...t })),
                            errors: { ...(drawerVenueDraft.errors || {}), trainers: undefined },
                          });
                        }}
                      >
                        {o.label}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })()}

            <Divider className="my-2" />
            <div className="d-flex justify-content-end" style={{ gap: 8 }}>
              <Button variant="outlined" onClick={closeDrawer}>
                Cancel
              </Button>
              <Button variant="contained" onClick={saveDrawerVenue}>
                Save location
              </Button>
            </div>
            </div>
          )}
        </MuiDialogContent>
      </MuiDialog>
    </div>
  );

  const renderStep4 = () => (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <Typography variant="subtitle2" className="text-primary font-weight-bold">
          Training materials
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={() => {
            const materials = [...trainingMaterials];
            materials.push(trainingMaterialDefaults);
            setTrainingMaterials(materials);
          }}
        >
          + Add material
        </Button>
      </div>

      {(trainingMaterials || []).map((material, index) => (
        <div key={index} className="row align-items-center">
          <div className="col-5">
            <TextField
              error={!!material.errors?.description}
              helperText={material.errors?.description || ""}
              size="small"
              className="mb-3"
              required
              type="text"
              fullWidth
              name="description"
              label="Description"
              placeholder="Description"
              variant="outlined"
              value={material.description}
              onChange={(event) => {
                const materials = [...trainingMaterials];
                materials[index] = {
                  ...material,
                  description: event.target.value,
                  errors: { ...material.errors, description: undefined },
                };
                setTrainingMaterials(materials);
              }}
            />
          </div>
          <div className="col-6">
            <TextField
              error={!!material.errors?.url}
              helperText={material.errors?.url || ""}
              size="small"
              className="mb-3"
              required
              type="url"
              fullWidth
              name="url"
              label="URL"
              placeholder="https://…"
              variant="outlined"
              value={material.url}
              onChange={(event) => {
                const materials = [...trainingMaterials];
                materials[index] = {
                  ...material,
                  url: event.target.value,
                  errors: { ...material.errors, url: undefined },
                };
                setTrainingMaterials(materials);
              }}
            />
          </div>
          <div className="col-1 text-right">
            <IconButton
              disabled={trainingMaterials.length <= 1}
              color="error"
              onClick={() => {
                const materials = [...trainingMaterials];
                materials.splice(index, 1);
                setTrainingMaterials(materials);
              }}
            >
              <Tooltip title="Remove material" placement="top">
                <span className="material-icons">close</span>
              </Tooltip>
            </IconButton>
          </div>
        </div>
      ))}

      <Divider className="my-3" />
      <Typography variant="subtitle2" className="text-primary font-weight-bold mb-2">
        Needs assessment
      </Typography>
      <TextField
        error={!!errors?.descriptionError}
        helperText={errors?.descriptionError || ""}
        multiline
        rows={3}
        fullWidth
        name="description"
        label="Describe the needs assessment"
        placeholder="Describe the needs assessment that informed this training…"
        value={needsAssessment.description}
        onChange={(e) => {
          setNeedsAssessment({ ...needsAssessment, description: e.target.value || "" });
          setErrors({ ...errors, descriptionError: "" });
        }}
      />

      <div className={`mt-3 ${!!errors.fileError ? "text-danger" : ""}`}>
        <Typography variant="caption" className="d-block mb-1">
          Supporting document (PDF)
        </Typography>

        {!!persistedNeedsAssessmentPath && !needsAssessment.file && (
          <Typography variant="caption" className="d-block text-muted mb-1">
            A PDF is already stored with this draft. You can submit as-is or choose a new
            file to replace it.
          </Typography>
        )}

        <div
          style={{
            border: "1px dashed #bdbdbd",
            borderRadius: 8,
            padding: 12,
            background: "#fff",
          }}
        >
          <Button component="label" fullWidth variant="outlined">
            Drag & drop or click to upload
            <input
              accept="application/pdf"
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                setNeedsAssessment({ ...needsAssessment, file: file || null });
                setErrors({ ...errors, fileError: "" });
              }}
            />
          </Button>
          {!!errors.fileError && (
            <Typography variant="caption" className="d-block mt-1">
              {errors.fileError}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => {
    const totalCohorts = trainingCohorts.length;
    const venuesAll = (trainingCohorts || []).flatMap((c) => c.venues || []);
    // Exclude the untouched placeholder row (and other empty shells) from review counts.
    const venues = venuesAll.filter((v) => !isVenuePlaceholder(v));
    const missingVenues = venues.filter((v) => !(v?.trainers || []).length);
    const missing = missingVenues.length;
    const assigned = venues.length - missing;
    return (
      <div>
        <Typography variant="subtitle2" className="text-primary font-weight-bold mb-2">
          Review before submitting
        </Typography>
        <div className="border rounded p-3 bg-white mb-3">
          <div className="row">
            <div className="col-6">
              <div className="text-muted small">Title</div>
              <div style={{ fontWeight: 700 }}>{title || "(not filled)"}</div>
            </div>
            <div className="col-6">
              <div className="text-muted small">CPD area</div>
              <div style={{ fontWeight: 700 }}>
                {providerProgram?.program?.name || "(not selected)"}
              </div>
            </div>
            <div className="col-6 mt-2">
              <div className="text-muted small">Course</div>
              <div style={{ fontWeight: 700 }}>{subProgram?.name || "(not selected)"}</div>
            </div>
            <div className="col-6 mt-2">
              <div className="text-muted small">Cohorts</div>
              <div style={{ fontWeight: 700 }}>{totalCohorts}</div>
            </div>
          </div>
        </div>

        <div className="border rounded p-3 bg-white">
          <Typography variant="subtitle2" className="font-weight-bold mb-2">
            Locations summary
          </Typography>
          <div className="d-flex flex-wrap" style={{ gap: 10 }}>
            <div className="px-2 py-1 border rounded" style={{ minWidth: 140 }}>
              <div className="text-muted small">Total locations</div>
              <div style={{ fontWeight: 700 }}>{venues.length}</div>
            </div>
            <div className="px-2 py-1 border rounded" style={{ minWidth: 160 }}>
              <div className="text-muted small">Trainers assigned</div>
              <div style={{ fontWeight: 700 }}>{assigned}</div>
            </div>
            <div
              className="px-2 py-1 border rounded"
              style={{
                minWidth: 160,
                background: missing > 0 ? "#FAEEDA" : "#fff",
                color: missing > 0 ? "#854F0B" : "inherit",
              }}
            >
              <div className="small" style={{ opacity: 0.85 }}>
                Missing trainers
              </div>
              <div style={{ fontWeight: 700 }}>{missing}</div>
            </div>
          </div>
          {missing > 0 && (
            <div
              className="mt-3 p-2 rounded"
              style={{ background: "#FAEEDA", color: "#854F0B" }}
            >
              {missing} locations still missing trainers — go back to step 3 to assign them.{" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => {
                  setCurrentStep(3);
                  setLocationFilter("missing");
                }}
              >
                Fix now
              </button>

              <div className="mt-2 text-dark" style={{ color: "#212529" }}>
                <Typography variant="caption" className="d-block">
                  Missing trainers on:
                </Typography>
                <ul className="mb-0" style={{ paddingLeft: 18 }}>
                  {missingVenues.slice(0, 5).map((v, idx) => (
                    <li key={idx}>
                      {(v?.district?.name || "-") +
                        " · " +
                        (v?.sector?.name || "-") +
                        " · " +
                        (v?.venue?.name || "(venue not selected)")}
                    </li>
                  ))}
                  {missingVenues.length > 5 && (
                    <li>{`…and ${missingVenues.length - 5} more`}</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const nextDisabled = loading || isLoading;
  const canBack = currentStep > 1;

  return (
    <Dialog onClose={onClose} open={showTrainingFormDialog} fullWidth maxWidth="xl">
      <DialogTitle className="text-primary">
        <span className="text-primary text-uppercase">New Training Application</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <span className="material-icons">close</span>
        </IconButton>

        <div className="mt-3">
          <StepIndicator step={currentStep} />
          <div className="mt-2">
            <ProgressBar step={currentStep} />
          </div>
        </div>
      </DialogTitle>

      <DialogContent>
        <div style={{ minHeight: "69vh" }} className="mt-2">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>
      </DialogContent>

      <DialogActions className="d-flex justify-content-between gap-2 flex-wrap px-3 py-3">
        <div>
          {canBack && (
            <Button
              variant="outlined"
              onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
              disabled={nextDisabled}
            >
              Back
            </Button>
          )}
        </div>

        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onSaveDraft}
            type="button"
            className="px-4"
            disabled={nextDisabled}
          >
            Save as draft
          </Button>

          {currentStep < 5 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCurrentStep((s) => Math.min(5, s + 1))}
              type="button"
              className="px-5"
              disabled={nextDisabled}
            >
              Next →
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              type="button"
              className="px-5"
              disabled={nextDisabled}
            >
              Submit
            </Button>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({
  user,
  loading,
  districts,
  providerPrograms,
  traineeCategories,
  schoolSubLevels,
  venueTypes,
}) => {
  return {
    user,
    loading,
    districts,
    providerPrograms,
    traineeCategories,
    schoolSubLevels,
    venueTypes,
  };
};

export default connect(mapStateToProps, { saveTraining, getProviderPrograms })(
  TrainingFormWizardDialog,
);

